import { taskSocketManager } from "../../../services/appRegistry";
import { socketRequest } from "../../../services/socketRequest";
import {
  CollectionDto,
  TaskInCollectionDto,
  UpdateCollectionDto,
} from "../collection/collection.types";
import { TaskDto } from "../types";
import {
  CreateProjectAccessDto,
  DeleteProjectAccessDto,
  FullProjectDto,
  ProjectAccessDto,
  ProjectDto,
  ProjectInfoDto,
  RemoveTaskFromProjectDto,
  UpdateProjectAccessDto,
  UpdateProjectDto,
  UpdatedColumnInProjectDto,
  UpdatedNoteInProjectDto,
  UpdatedNoteInProjectReturnDto,
  CreateProjectMilestoneDto,
  UpdateProjectMilestoneDto,
  DeleteProjectMilestoneDto,
  ProjectMilestoneAnswerDto,
  UpdateProjectStatusDto,
  ProjectStatusAnswerDto,
} from "./project.types";

const request = <T>(
  event: string,
  payload?: unknown,
  options?: Parameters<typeof socketRequest<T>>[3],
) => socketRequest<T>(taskSocketManager, event, payload, options);

// Answers that wrap the result in "data"
const unwrapData = { transform: (response: any) => response.data };

const createQuickProject = (): Promise<ProjectInfoDto> =>
  request("createQuickProject");

const addTaskToProject = (
  taskId: string,
  projectId: string,
): Promise<TaskInCollectionDto> =>
  request("addTaskToProject", { taskId, projectId });

const removeTaskFromProject = (
  taskId: string,
  collectionId: string,
  projectId: string,
): Promise<RemoveTaskFromProjectDto> =>
  request("removeTaskFromProject", {
    projectId: projectId,
    collectionId: collectionId,
    taskId: taskId,
  });

const getProjects = (
  groupId?: string,
  organizationId?: string,
): Promise<ProjectInfoDto[]> =>
  request("getProjects", { groupId, organizationId });

const getProject = (projectId?: string): Promise<FullProjectDto> =>
  request("getProject", { projectId });

const deleteProject = (projectId: string): Promise<string> =>
  request("deleteProject", { projectId: projectId });

const updateProject = (
  updateProjectData: UpdateProjectDto,
): Promise<ProjectDto> =>
  request("updateProject", {
    updateProjectData: updateProjectData,
    projectId: updateProjectData.id,
  });

const updateCollectionInProject = (
  projectId: string,
  updateCollectionData: UpdateCollectionDto,
): Promise<CollectionDto> =>
  request("updateCollectionInProject", {
    projectId,
    updateCollectionData: updateCollectionData,
  });

const updateNotePositionsInProject = (
  updatedNotes: UpdatedNoteInProjectDto[],
  projectId: string,
): Promise<UpdatedNoteInProjectReturnDto[]> =>
  request(
    "updateNotePositionsInProject",
    { updatedNotes, projectId },
    unwrapData,
  );

const createCollectionInProject = (projectId: string): Promise<CollectionDto> =>
  request("createCollectionInProject", { projectId: projectId });

const deleteCollectionInProject = (
  projectId: string,
  collectionId: string,
): Promise<string> =>
  request("deleteCollectionInProject", { projectId, collectionId });

const updateColumnPositionsInProject = (
  updatedColumns: UpdatedColumnInProjectDto[],
  projectId: string,
): Promise<UpdatedColumnInProjectDto[]> =>
  request(
    "updateColumnPositionsInProject",
    { updatedColumns, projectId },
    unwrapData,
  );

const createProjectAccess = (
  createTaskAccessData: CreateProjectAccessDto,
): Promise<ProjectAccessDto> =>
  request("createProjectAccess", createTaskAccessData);

const deleteProjectAccess = (
  deleteTaskAccessData: DeleteProjectAccessDto,
): Promise<DeleteProjectAccessDto> =>
  request("deleteProjectAccess", deleteTaskAccessData);

const getProjectTasks = (): Promise<TaskDto[]> => request("getProjectTasks");

const updateProjectAcccess = (
  dto: UpdateProjectAccessDto,
): Promise<ProjectAccessDto> => request("updateProjectAccess", dto);

// Milestones and status: one request, answered by "<event>Answer"
const createProjectMilestone = (data: CreateProjectMilestoneDto) =>
  request<ProjectMilestoneAnswerDto>("createProjectMilestone", data);

const updateProjectMilestone = (data: UpdateProjectMilestoneDto) =>
  request<ProjectMilestoneAnswerDto>("updateProjectMilestone", data);

const deleteProjectMilestone = (data: DeleteProjectMilestoneDto) =>
  request<DeleteProjectMilestoneDto>("deleteProjectMilestone", data);

const updateProjectStatus = (data: UpdateProjectStatusDto) =>
  request<ProjectStatusAnswerDto>("updateProjectStatus", data);

// Rows

const projectService = {
  updateProjectStatus,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  createQuickProject,
  createProjectAccess,
  getProjects,
  getProject,
  deleteCollectionInProject,
  updateProject,
  addTaskToProject,
  deleteProject,
  removeTaskFromProject,
  updateNotePositionsInProject,
  updateCollectionInProject,
  createCollectionInProject,
  updateColumnPositionsInProject,
  deleteProjectAccess,
  getProjectTasks,
  updateProjectAcccess,
};
export default projectService;
